import { AlertCircle } from 'lucide-react';

export function getBpmTenantConfigTabs(config, probeFailed = []) {
  if (!config) return [];
  return [
    {
      id: 'inferred',
      label: `Inferred routing (${(config.inferredRouting || []).length})`,
    },
    { id: 'pod', label: `Pod REST (${(config.liveEndpoints || []).length})` },
    { id: 'rules', label: `Rules (${(config.rules || []).length})` },
    { id: 'groups', label: `Groups (${(config.approvalGroups || []).length})` },
    {
      id: 'directory',
      label: `Approver directory (${(config.approverDirectory || config.approvalUsers || []).length})`,
    },
    {
      id: 'runtime',
      label: `BPM runtime (${(config.bpmTasks || []).length + (config.notifications || []).length})`,
    },
    { id: 'transactions', label: `Transactions (${(config.transactions || []).length})` },
    ...(probeFailed.length > 0
      ? [{ id: 'probe', label: `Probe (${probeFailed.length} empty/404)` }]
      : []),
  ];
}

function TenantConfigSectionContent({ config, section, probeFailed }) {
  if (section === 'inferred') {
    return (
      <ul className="divide-y divide-slate-100 p-2">
        {(config.inferredRouting || []).length === 0 ? (
          <li className="p-2 text-slate-500">No inferred routing rows.</li>
        ) : (
          config.inferredRouting.map((row) => (
            <li key={row.taskDefinitionName || row.instanceCount} className="px-2 py-1.5 text-slate-700">
              <span className="font-medium text-slate-800">{row.taskDefinitionName || 'Task'}</span>
              {' · '}
              {row.instanceCount} instance(s)
            </li>
          ))
        )}
      </ul>
    );
  }

  if (section === 'rules') {
    return (
      <ul className="divide-y divide-slate-100 p-2">
        {(config.rules || []).length === 0 ? (
          <li className="p-2 text-slate-500">No approval rules returned.</li>
        ) : (
          config.rules.slice(0, 80).map((rule, i) => (
            <li key={rule.id || rule.name || i} className="px-2 py-1.5 text-slate-700">
              <span className="font-medium">{rule.name || rule.ruleName || 'Rule'}</span>
              {rule.status && <span className="text-slate-500"> · {rule.status}</span>}
            </li>
          ))
        )}
      </ul>
    );
  }

  if (section === 'groups') {
    return (
      <ul className="divide-y divide-slate-100 p-2">
        {(config.approvalGroups || []).length === 0 ? (
          <li className="p-2 text-slate-500">No approval groups.</li>
        ) : (
          config.approvalGroups.slice(0, 80).map((g, i) => (
            <li key={g.name || g.id || i} className="px-2 py-1.5 text-slate-700">
              {g.name || g.displayName || JSON.stringify(g).slice(0, 80)}
            </li>
          ))
        )}
      </ul>
    );
  }

  if (section === 'directory') {
    return (
      <ul className="divide-y divide-slate-100 p-2">
        {(config.approverDirectory || config.approvalUsers || []).length === 0 ? (
          <li className="p-2 text-slate-500">No approver directory entries.</li>
        ) : (
          (config.approverDirectory || config.approvalUsers).slice(0, 80).map((u, i) => (
            <li key={u.personNumber || u.username || i} className="px-2 py-1.5 text-slate-700">
              {u.displayName || u.name || u.personNumber || u.username}
            </li>
          ))
        )}
      </ul>
    );
  }

  if (section === 'runtime') {
    return (
      <div className="p-2 space-y-2">
        {(config.bpmTasks || []).slice(0, 40).map((t, i) => (
          <p key={i} className="rounded bg-slate-50 px-2 py-1.5 text-slate-700">
            <span className="font-medium">{t.taskDefinitionName || '—'}</span>
            {t.state && <span> · {t.state}</span>}
          </p>
        ))}
        {(config.notifications || []).slice(0, 20).map((n, i) => (
          <p key={`n-${i}`} className="rounded bg-slate-50 px-2 py-1.5 text-slate-700">
            <span className="font-medium">{n.taskDefinitionName || '—'}</span>
            {n.workFlowPattern && <span> · {n.workFlowPattern}</span>}
          </p>
        ))}
      </div>
    );
  }

  if (section === 'transactions') {
    return (
      <div className="p-2 space-y-2">
        {Object.entries(config.transactionsByProcess || {}).map(([proc, txs]) => (
          <div key={proc}>
            <p className="font-semibold text-slate-800 border-b border-slate-100 pb-1 mb-1">{proc}</p>
            <ul className="space-y-1">
              {txs.map((t, i) => (
                <li key={i} className="rounded bg-slate-50 px-2 py-1.5 text-slate-700">
                  <span className="font-medium">{t.status || '—'}</span>
                  {t.assignee && <span> · assignee: {t.assignee}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  if (section === 'pod') {
    return (
      <div className="p-2 text-[11px] text-slate-600">
        {(config.liveEndpoints || []).length === 0 ? (
          <p>No workflow REST collections returned data on this pod.</p>
        ) : (
          <ul className="list-disc pl-4 space-y-1 font-mono break-all">
            {config.liveEndpoints.map((path) => (
              <li key={path}>{path}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (section === 'probe') {
    return (
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-slate-100 text-left text-slate-500">
            <th className="p-2 font-medium">Status</th>
            <th className="p-2 font-medium">Items</th>
            <th className="p-2 font-medium">Endpoint</th>
          </tr>
        </thead>
        <tbody>
          {probeFailed.map((e, i) => (
            <tr key={i} className="border-b border-slate-50">
              <td className="p-2 text-amber-700">{e.status || '—'}</td>
              <td className="p-2">{e.itemCount ?? 0}</td>
              <td className="p-2 break-all text-slate-600">{e.path}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return <p className="p-3 text-slate-500">Select a configuration section.</p>;
}

export default function BpmTenantApprovalConfigPanel({
  config,
  error,
  loading,
  section,
  onSectionChange,
  probeFailed = [],
  showNav = true,
  showHeader = true,
  contentClassName = 'max-h-[min(40vh,400px)]',
}) {
  if (loading && !config && !error) {
    return <p className="text-xs text-slate-500">Loading tenant approval configuration…</p>;
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-800">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span className="break-words">{error}</span>
      </div>
    );
  }

  if (!config) {
    return <p className="text-xs text-slate-500">Refresh tenant data to load configuration.</p>;
  }

  const tabs = getBpmTenantConfigTabs(config, probeFailed);

  return (
    <div className="space-y-2 text-xs">
      {showHeader && (config.hints || []).length > 0 && (
        <p
          className="text-[11px] text-amber-900 line-clamp-2"
          title={(config.hints || []).join(' ')}
        >
          {(config.hints || [])[0]}
        </p>
      )}

      {showNav && (
        <div className="flex flex-wrap gap-1.5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onSectionChange(tab.id)}
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                section === tab.id
                  ? 'bg-violet-700 text-white'
                  : 'border border-violet-200 bg-white text-violet-900 hover:bg-violet-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div
        className={`overflow-y-auto rounded-md border border-violet-100 bg-white/80 ${contentClassName}`}
      >
        <TenantConfigSectionContent config={config} section={section} probeFailed={probeFailed} />
      </div>
    </div>
  );
}
